import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FloatingInput } from "@/components/ui/floating-input";
import { FloatingSelect } from "@/components/ui/floating-select";
import { api, edenFetch } from "@/lib/eden";
import { queryClient } from "@/lib/queryClient";
import { detectTimezone } from "@shared/account-data";
import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useId, useRef, useState } from "react";
import { FaBirthdayCake } from "react-icons/fa";

import {
  emailLinkReturnPath,
  MONTH_OPTIONS,
  YEAR_OPTIONS,
  dayOptionsFor,
  isValidEmail,
  mapAuthError,
  validateBirthday,
} from "./auth-utils";
import { FormErrorMessage } from "./form-error";
import { PasswordField } from "./password-field";

interface RegisterFormProps {
  onSwitch?: () => void;
  initialError?: string;
  /** Prefill (an address the page's email link proves — lib/proven-email). */
  defaultEmail?: string;
}

/**
 * Port of legacy `components/auth/register`.
 *
 * Same fields in the same order — Full name, Email, Password, the Month/Day/Year
 * birthday row under a cake-icon heading, "Keep my age secret", the terms
 * sentence, then the button. The birthday and the keep-age-secret flag ride
 * along to `POST /public/auth/email/register`, which lands them on the member's
 * self-contact + a birthday reminder (the new home for what the legacy `member`
 * row carried inline).
 *
 * One deliberate divergence: legacy allowed 120 characters per name part, while
 * `user.display_name` is VARCHAR(50). We validate against the real column limit
 * rather than let the server reject a form the client called valid.
 */
export function RegisterForm({ onSwitch, initialError, defaultEmail }: RegisterFormProps) {
  const cakeLabelId = useId();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [password, setPassword] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  const [keepAgeSecret, setKeepAgeSecret] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [general, setGeneral] = useState(initialError ?? "");
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const birthdayRef = useRef<HTMLDivElement>(null);

  const errors = {
    fullName: !fullName.trim()
      ? "Name is required!"
      : fullName.trim().length > 50
        ? "Name must be 50 characters or less"
        : undefined,
    email: !email.trim()
      ? "Email is required!"
      : !isValidEmail(email)
        ? "Email is invalid"
        : undefined,
    password: !password
      ? "Password is required!"
      : password.length < 8
        ? "Password must be at least 8 characters"
        : password.length > 120
          ? "Password must be at most 120 characters"
          : undefined,
    month: !month ? "Required" : undefined,
    day: !day ? "Required" : undefined,
    year: !year ? "Required" : undefined,
    // Impossible dates, future dates and the 13-year cutoff — the same rules the
    // server applies, checked here so they land on the birthday row instead of
    // arriving as a general error after a round trip.
    birthday: validateBirthday(month, day, year),
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const birthdayTouched = !!(touched.month && touched.day && touched.year);

  const registerMutation = useMutation({
    mutationFn: () =>
      edenFetch(
        api.api.public.auth.email.register.post({
          email,
          password,
          display_name: fullName.trim(),
          birthday: `${year}-${month}-${day}`,
          is_age_hidden: keepAgeSecret,
          platform: "web",
          timezone: detectTimezone(),
          // BDAY-479: where the verify email's button brings them back.
          return_to: emailLinkReturnPath(),
        }),
      ),
    onSuccess: (data) => {
      // Hand the just-issued verification id to /email-verify. Without this it
      // finds nothing in the cache and immediately requests a SECOND code, so
      // every signup sent two verification emails and the first one — the one
      // that usually arrives first — was already superseded.
      queryClient.setQueryData(["pending-email-verification"], {
        email_verification_id: data.email_verification_id,
        email: email.trim().toLowerCase(),
      });
      // __root's logged-in branch performs the redirect (honouring `?next=`).
      void queryClient.invalidateQueries({ queryKey: ["/api/user/auth/me"] });
    },
    onError: (err: Error) => setGeneral(mapAuthError(err.message)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ fullName: true, email: true, password: true, month: true, day: true, year: true });
    setGeneral("");
    if (hasErrors) {
      const target = errors.fullName
        ? nameRef.current
        : errors.email
          ? emailRef.current
          : errors.password
            ? passwordRef.current
            : birthdayRef.current;
      target?.focus();
      return;
    }
    registerMutation.mutate();
  };

  return (
    <>
      {/* noValidate — see login-form. Doubly important here: FloatingSelect's
          `required` reaches a visually-hidden 1px input, so a native bubble would
          be anchored to something invisible and the button would just appear dead. */}
      <form
        noValidate
        onSubmit={submit}
        className="flex w-full max-w-[500px] flex-col items-center"
      >
        <div className="mb-[15px] w-full">
          <FloatingInput
            ref={nameRef}
            label="Full name"
            id="fullName"
            name="fullName"
            autoComplete="name"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, fullName: true }))}
            error={touched.fullName ? errors.fullName : undefined}
            data-testid="input-name-signup"
          />
        </div>
        <div className="mb-[15px] w-full">
          <FloatingInput
            ref={emailRef}
            label="Email"
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            error={touched.email ? errors.email : undefined}
            data-testid="input-email-signup"
          />
        </div>
        <div className="mb-[15px] w-full">
          <PasswordField
            ref={passwordRef}
            label="Password"
            id="password"
            name="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            error={touched.password ? errors.password : undefined}
            data-testid="input-password-signup"
          />
        </div>

        <div
          id={cakeLabelId}
          className="mb-[5px] flex w-full items-center text-[#2b2b2b] uppercase"
          style={{ lineHeight: "23px" }}
        >
          <FaBirthdayCake className="mt-[-3px] mr-2.5 text-primary" aria-hidden />
          <span>Birthday</span>
        </div>
        <div
          ref={birthdayRef}
          tabIndex={-1}
          className="flex w-full gap-2.5 outline-none"
          role="group"
          aria-labelledby={cakeLabelId}
        >
          <div className="min-w-[calc(46%-20px)] flex-1">
            <FloatingSelect
              label="Month"
              id="month"
              name="month"
              required
              items={MONTH_OPTIONS}
              value={month || null}
              onValueChange={(v) => {
                setMonth(v);
                // 31 → February: drop a day the new month doesn't have.
                if (day && Number(day) > dayOptionsFor(v, year).length) setDay("");
                setTouched((t) => ({ ...t, month: true }));
              }}
              error={touched.month ? errors.month : undefined}
            />
          </div>
          <div className="min-w-[26%] flex-1">
            <FloatingSelect
              label="Day"
              id="day"
              name="day"
              required
              items={dayOptionsFor(month, year)}
              value={day || null}
              onValueChange={(v) => {
                setDay(v);
                setTouched((t) => ({ ...t, day: true }));
              }}
              error={touched.day ? errors.day : undefined}
            />
          </div>
          <div className="min-w-[28%] flex-1">
            <FloatingSelect
              label="Year"
              id="year"
              name="year"
              required
              items={YEAR_OPTIONS}
              value={year || null}
              onValueChange={(v) => {
                setYear(v);
                // Feb 29 → a common year.
                if (day && Number(day) > dayOptionsFor(month, v).length) setDay("");
                setTouched((t) => ({ ...t, year: true }));
              }}
              error={touched.year ? errors.year : undefined}
            />
          </div>
        </div>

        {birthdayTouched && errors.birthday && (
          <p role="alert" className="mt-1 w-full text-xs text-destructive">
            {errors.birthday}
          </p>
        )}

        <label className="mt-[15px] mb-[15px] flex w-full cursor-pointer items-center gap-2.5 font-normal">
          <Checkbox
            id="keepAgeSecret"
            name="keepAgeSecret"
            checked={keepAgeSecret}
            onCheckedChange={(checked) => setKeepAgeSecret(checked === true)}
          />
          <span>Keep my age secret</span>
        </label>

        <p className="mb-[15px] w-full">
          By clicking the button below you agree to our{" "}
          <Link to="/terms" target="_blank" className="text-primary underline">
            terms and conditions
          </Link>
        </p>

        <div>
          <Button
            id="register-button"
            type="submit"
            size="compact"
            className="min-h-[39px] w-[199px] text-sm tracking-normal normal-case"
            disabled={registerMutation.isPending}
            data-testid="button-email-signup"
          >
            {registerMutation.isPending && <Loader2 className="animate-spin" aria-hidden />}
            Register with Email
          </Button>
        </div>

        {general && <FormErrorMessage>{general}</FormErrorMessage>}
      </form>

      {onSwitch && (
        // mt-4: legacy's `.register-container` carried `padding: 1rem 0` around the
        // form, which is what separates the submit button from this line on prod.
        // The form and this block are adjacent siblings here, so without it the
        // switch line sits flush against the button.
        <div className="mt-4 flex items-center justify-center gap-1.5 text-sm">
          <span>Already have an account:</span>
          <button
            type="button"
            onClick={onSwitch}
            className="text-primary underline hover:text-primary/80"
            data-testid="button-switch-signin"
          >
            Sign In
          </button>
        </div>
      )}
    </>
  );
}
